import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

// Reusable confirm-and-delete control. Renders a trash icon button (or custom children)
// that opens a confirmation dialog, then DELETEs `endpoint` and calls onDeleted().
export function DeleteButton({ endpoint, label = "item", name, onDeleted, testId, children, className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success(`Deleted ${label}`);
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `Could not delete ${label}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {children || (
          <button data-testid={testId} title={`Delete ${label}`} className={`text-zinc-400 hover:text-red-600 align-middle ${className}`}>
            <Trash2 className="w-4 h-4 inline" />
          </button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            {name ? <>You're about to permanently delete <b>{name}</b>. </> : null}
            This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
